import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  SxProps,
  Theme,
  Typography
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';

import { PreviewImageTooltip } from '@/components/PreviewImageTooltip';
import { preventScrollPropagation } from '@/utils/preventScrollPropagation';

import { GroupedOption, Option, Options } from './types';
import useSelectMenu from './useSelectMenu';

const isGroupedOption = (option: Option | GroupedOption): option is GroupedOption => {
  return 'options' in option;
};

type GroupedSelectProps = {
  label: string;
  value: string | null;
  onChange: (value: Option) => void;
  options: Options;
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  testId?: string;
  sx?: SxProps<Theme>;
};

const GroupedSelect = ({
  label,
  value,
  onChange,
  options,
  disabled = false,
  placeholder = '',
  isLoading = false,
  testId,
  sx
}: GroupedSelectProps) => {
  const groupedOptions = options.filter(isGroupedOption);
  const directOptions = options.filter(option => !isGroupedOption(option)) as Option[];

  const flatOptions: Option[] = [
    ...directOptions,
    ...groupedOptions.flatMap(group => group.options)
  ];

  const { handleMenuItemClick, selected } = useSelectMenu({
    options: flatOptions,
    onChange,
    value
  });

  const [openSelect, setOpenSelect] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [renderAbove, setRenderAbove] = useState(false);

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (openSelect && groupedOptions.length > 0 && selected) {
      const selectedGroup = groupedOptions.find(group =>
        group.options.some(option => option.value === selected.value)
      );
      setOpenGroup(selectedGroup?.label ?? null);
    }
    // Disable exhaustive-deps to run this effect only when dropdown opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSelect]);

  useLayoutEffect(() => {
    if (openSelect && triggerRef.current && dropdownRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      const dropdownHeight = Math.min(300, dropdownRef.current.scrollHeight);

      // Flip above if not enough space below and more room above
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setRenderAbove(true);
        dropdownRef.current.style.maxHeight = `${spaceAbove - 10}px`;
      } else {
        setRenderAbove(false);
        dropdownRef.current.style.maxHeight = `${spaceBelow - 10}px`;
      }
    }
  }, [openSelect]);

  const handleGroupToggle = (groupLabel: string) => {
    setOpenGroup(prev => (prev === groupLabel ? null : groupLabel));
  };

  const handleClickAway = () => {
    setOpenSelect(false);
  };

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    if (openSelect && dropdownRef.current) {
      cleanup = preventScrollPropagation(dropdownRef.current);
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, [openSelect]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        component={Paper}
        data-testid={testId ? `setting-changer-${testId}` : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          p: 3,
          width: '100%',
          gap: 1,
          ...sx
        }}
      >
        {openSelect && (
          <Box
            sx={{
              position: 'fixed',
              width: '100vw',
              height: '100vh',
              left: 0,
              top: 0,
              background: 'rgba(18, 22, 63, 0.40)',
              backdropFilter: 'blur(1px)',
              zIndex: 9999
            }}
            onClick={handleClickAway}
          />
        )}
        <Typography
          className='label__container'
          sx={{
            width: {
              xs: 'auto',
              md: '40%'
            }
          }}
          variant='body2'
        >
          {label}
        </Typography>
        <Box sx={{ minWidth: '250px', position: 'relative' }}>
          <Box
            ref={triggerRef}
            data-testid={testId ? `select-${testId}` : undefined}
            sx={theme => ({
              border: `1px solid ${disabled ? theme.palette.action.disabled : theme.palette.text.primary}`,
              paddingLeft: 1,
              paddingRight: 0.5,
              cursor: disabled || isLoading ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: '30px',
              color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
              backgroundColor: disabled ? theme.palette.action.disabledBackground : 'inherit'
            })}
            onClick={() => {
              if (!disabled && !isLoading) {
                setOpenSelect(prev => !prev);
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
              {isLoading && (
                <CircularProgress
                  data-testid={testId ? `loading-${testId}` : undefined}
                  size={16}
                  thickness={4}
                />
              )}
              <Box
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {selected ? selected.label : placeholder}
              </Box>
              {selected?.info}
            </Box>
            <ArrowDropDownIcon
              sx={theme => ({
                transform: openSelect ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
                cursor: disabled || isLoading ? 'default' : 'pointer',
                color: disabled ? theme.palette.text.disabled : 'inherit'
              })}
            />
          </Box>
          {openSelect && (
            <Box
              ref={dropdownRef}
              sx={{
                position: 'absolute',
                [renderAbove ? 'bottom' : 'top']: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 999999,
                overflowY: 'auto',
                backgroundColor: 'background.paper',
                border: '1px solid rgba(0,0,0,0.12)',
                minWidth: '100%'
              }}
            >
              {directOptions.map((option, index) => (
                <PreviewImageTooltip
                  key={option.value + index}
                  height={option.height}
                  previewImage={option.previewImage}
                >
                  <MenuItem
                    selected={selected?.value === option.value}
                    sx={{ pl: 2, gap: 1, display: 'flex' }}
                    onClick={() => {
                      handleMenuItemClick(flatOptions.indexOf(option));
                      setOpenSelect(false);
                    }}
                  >
                    {option.label} {option?.info}
                  </MenuItem>
                </PreviewImageTooltip>
              ))}
              {groupedOptions.map(group => (
                <Box key={group.label}>
                  <ListItemButton
                    onClick={() => {
                      handleGroupToggle(group.label);
                    }}
                  >
                    <ListItemText primary={group.label} />
                    {openGroup === group.label ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse
                    unmountOnExit
                    in={openGroup === group.label}
                    timeout='auto'
                  >
                    <List disablePadding>
                      {group.options.map((option, index) => (
                        <PreviewImageTooltip
                          key={option.value + index}
                          height={option.height}
                          previewImage={option.previewImage}
                        >
                          <MenuItem
                            selected={selected?.value === option.value}
                            sx={{ pl: 4 }}
                            onClick={() => {
                              handleMenuItemClick(flatOptions.indexOf(option));
                              setOpenSelect(false);
                            }}
                          >
                            {option.label}
                          </MenuItem>
                        </PreviewImageTooltip>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export default GroupedSelect;
